import dayjs, { Dayjs } from "dayjs";

export type DateInput = Dayjs | Date | string;

export type DateFormatOptions = {
  withWeekday?: boolean;
  locale?: "en" | "it";
};

const DATE_PATTERN = "D MMM YYYY";
const DATE_WITHOUT_YEAR_PATTERN = "D MMM";
const WEEKDAY_PATTERN = "ddd";
const TIME_PATTERN = "HH:mm";

function toDayjs(date: DateInput, locale: DateFormatOptions["locale"]): Dayjs {
  const day = dayjs.isDayjs(date) ? date : dayjs(date);
  return day.locale(locale ?? "en");
}

function withOptionalWeekday(pattern: string, withWeekday?: boolean): string {
  return withWeekday ? `${WEEKDAY_PATTERN} ${pattern}` : pattern;
}

export function formatDate(
  date: DateInput,
  { withWeekday, locale }: DateFormatOptions = {},
): string {
  return toDayjs(date, locale).format(
    withOptionalWeekday(DATE_PATTERN, withWeekday),
  );
}

export function formatTime(date: DateInput): string {
  return toDayjs(date, "en").format(TIME_PATTERN);
}

export function formatDateTime(
  date: DateInput,
  options: DateFormatOptions = {},
): string {
  return `${formatDate(date, options)}, ${formatTime(date)}`;
}

export function formatDateRange(
  from: DateInput,
  to: DateInput,
  { withWeekday, locale }: DateFormatOptions = {},
): string {
  const fromDay = toDayjs(from, locale);
  const toDay = toDayjs(to, locale);
  const fromPattern =
    fromDay.year() === toDay.year() ? DATE_WITHOUT_YEAR_PATTERN : DATE_PATTERN;
  return `${fromDay.format(withOptionalWeekday(fromPattern, withWeekday))} – ${toDay.format(withOptionalWeekday(DATE_PATTERN, withWeekday))}`;
}
